import { useTranslation } from "react-i18next";import React, { useState } from 'react';
import { Plus, Edit, Trash2, Power, Copy } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Badge } from '@/shared/components/ui/badge';
import { Button } from '@/shared/components/ui/button';
import { Switch } from '@/shared/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs';

export const NotificationRules = () => {const { t } = useTranslation("ezparcel");
  const [rules, setRules] = useState(notificationRules);

  const toggleRule = (ruleId) => {
    setRules(rules.map((rule) =>
    rule.id === ruleId ? { ...rule, enabled: !rule.enabled } : rule
    ));
  };

  const activeRules = rules.filter((r) => r.enabled);
  const inactiveRules = rules.filter((r) => !r.enabled);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t("notificationRules", "Notification Rules")}</h1>
          <p className="text-muted-foreground mt-2">{t("configureEventdrivenNotifications", "Configure event-driven notifications for Telegram and WhatsApp")}

          </p>
        </div>
        <Button className="bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent-hover))]">
          <Plus className="mr-2 h-4 w-4" />{t("createNewRule", "Create New Rule")}

        </Button>
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="p-6">
            <p className="text-sm text-muted-foreground">{t("totalRules", "Total Rules")}</p>
            <p className="text-3xl font-bold mt-2">{rules.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <p className="text-sm text-muted-foreground">{t("activeRules", "Active Rules")}</p>
            <p className="text-3xl font-bold mt-2 text-green-600">{activeRules.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <p className="text-sm text-muted-foreground">{t("messagesToday", "Messages Today")}</p>
            <p className="text-3xl font-bold mt-2">
              {rules.reduce((sum, r) => sum + r.usage.last24h, 0)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <p className="text-sm text-muted-foreground">{t("costControls", "Cost Controls")}</p>
            <p className="text-3xl font-bold mt-2 text-blue-600">
              {rules.filter((r) => r.costControl.enabled).length}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Rules List */}
      <Tabs defaultValue="all">
        <TabsList>
          <TabsTrigger value="all">{t("allRules", "All Rules (")}{rules.length})</TabsTrigger>
          <TabsTrigger value="customer">{t("customerUpdates", "Customer Updates")}</TabsTrigger>
          <TabsTrigger value="operations">{t("operationsAlerts", "Operations Alerts")}</TabsTrigger>
          <TabsTrigger value="inactive">{t("inactive", "Inactive (")}{inactiveRules.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="space-y-4 mt-6">
          {rules.map((rule) => {
            const template = notificationTemplates.find((t) => t.id === rule.template);
            const channelBadge = rule.channels[0].channel === 'telegram' ?
            { color: 'bg-blue-100 text-blue-700', label: 'Telegram' } :
            { color: 'bg-green-100 text-green-700', label: 'WhatsApp' };

            return (
              <Card key={rule.id} className={`card-elevated ${!rule.enabled ? 'opacity-60' : ''}`}>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-semibold">{rule.name}</h3>
                        <Badge className={channelBadge.color}>
                          {channelBadge.label}
                        </Badge>
                        {rule.costControl.enabled &&
                        <Badge variant="outline" className="border-orange-500 text-orange-700">{t("costControl", "Cost Control")}

                        </Badge>
                        }
                        <Switch
                          checked={rule.enabled}
                          onCheckedChange={() => toggleRule(rule.id)} />
                        
                      </div>

                      <div className="grid grid-cols-2 gap-4 mt-4">
                        <div>
                          <p className="text-sm text-muted-foreground">{t("triggerEvent", "Trigger Event")}</p>
                          <p className="font-medium text-sm mt-1">
                            {rule.trigger.event.replace('_', ' ')} 
                            {rule.trigger.status && ` → ${rule.trigger.status}`}
                            {rule.trigger.threshold && ` (${rule.trigger.threshold})`}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">{t("recipients", "Recipients")}</p>
                          <p className="font-medium text-sm mt-1">
                            {rule.recipients.map((r) => r.role).join(', ')}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">{t("template", "Template")}</p>
                          <p className="font-medium text-sm mt-1">{template?.name || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">{t("usageLast24h", "Usage (Last 24h)")}</p>
                          <p className="font-medium text-sm mt-1">{rule.usage.last24h}{t("messages", "messages")}</p>
                        </div>
                      </div>

                      {rule.costControl.enabled &&
                      <div className="mt-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                          <p className="text-sm font-medium text-orange-900">{t("costLimits", "Cost Limits")}</p>
                          <div className="flex gap-4 mt-2 text-sm">
                            <span>{t("maxPerDay", "Max per day:")}{rule.costControl.maxPerDay}</span>
                            <span>{t("maxPerShipment", "Max per shipment:")}{rule.costControl.maxPerShipment}</span>
                          </div>
                        </div>
                      }
                    </div>

                    <div className="flex gap-2">
                      <Button variant="outline" size="sm">
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button variant="outline" size="sm">
                        <Copy className="h-4 w-4" />
                      </Button>
                      <Button variant="outline" size="sm" className="text-red-600">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>);

          })}
        </TabsContent>
      </Tabs>
    </div>);

};